import { FC, memo } from "react";
import { styled } from "@mui/material";
import { books, mailTo } from "../../utils/landing-data";
import { bg, bp, fluid, font, w } from "../../styles/landing-tokens";
import { Container, DisplayHeading, Eyebrow, PrimaryButton } from "./shared";
import { ArrowUpRight, QuoteIcon } from "./icons";
import { useDraw, useReveal } from "./reveal";

type BookData = (typeof books)[number];

const Section = styled("section")({
  background: bg,
  /** Figma: recordings end at y=8679, this heading sits at y=8995. */
  paddingTop: 315,
  paddingBottom: 0,

  [bp.md]: { paddingTop: 140, paddingBottom: 0 },
});

/** Figma places the heading at x=220, indented from the 102px content edge. */
const Heading = styled(DisplayHeading)({
  marginLeft: 118,

  [bp.lg]: { marginLeft: 0 },
});

/**
 * Figma lays the two books out asymmetrically on the 1715px column:
 *   book 1 — cover 804 at x=102, copy 917 at x=803
 *   book 2 — copy 917 at x=369, cover 788 at x=1029 (overlapping the copy track)
 * The overlap is safe because the copy itself never exceeds 727px.
 */
const Book = styled("div")<{ flip?: boolean }>(({ flip }) => ({
  display: "grid",
  gridTemplateColumns: flip ? "917px 531px" : "700px 917px",
  alignItems: "start",
  /**
   * Figma: book 1 starts 104px below the heading, book 2 115px below book 1 —
   * book 2 trimmed by 5px to absorb rounding accumulated inside book 1.
   */
  marginTop: flip ? 110 : 104,
  maxWidth: flip ? 1448 : "none",
  marginLeft: flip ? "auto" : 0,

  [bp.lg]: {
    gridTemplateColumns: "1fr 1.1fr",
    columnGap: 40,
    maxWidth: "none",
  },
  [bp.md]: {
    gridTemplateColumns: "1fr",
    rowGap: 40,
  },
}));

/**
 * The aspect ratio is declared so the lazily-loaded cover reserves its box and
 * the page height stays stable (Figma: 804×742 and 788×727 — both ~1.084).
 */
const Cover = styled("img")({
  width: "100%",
  aspectRatio: "804 / 742",
  objectFit: "contain",

  [bp.md]: { maxWidth: 420, margin: "0 auto" },
});

/**
 * On mobile the cover always comes first, regardless of desktop side.
 * When flipped, the 788px cover overhangs its 531px track to the left.
 */
const CoverCell = styled("div")<{ order?: number; overhang?: boolean }>(
  ({ order = 0, overhang }) => ({
    order,
    /** Both covers overhang their track so the copy lands on its Figma x. */
    width: overhang ? 788 : 804,
    marginLeft: overhang ? -257 : 0,
    /** Figma: book 1's cover sits 9px below the block top; book 2's is flush. */
    marginTop: overhang ? 0 : 9,

    [bp.lg]: { width: "auto", marginLeft: 0, marginTop: 0 },
    [bp.md]: { order: 0 },
  })
);

/** Figma: the copy starts 40px (book 1) / 58px (book 2) below the block top. */
const CopyCell = styled("div")<{ order?: number; offset?: number }>(
  ({ order = 1, offset = 40 }) => ({
    order,
    maxWidth: 917,
    paddingTop: offset,

    [bp.md]: { order: 1, paddingTop: 0 },
  })
);

const Year = styled(Eyebrow)({
  fontWeight: 500,
  fontSize: 13.8,
  letterSpacing: "0.18em",
  color: w(0.4),
});

/** Figma: 40px on book 1, 45px on book 2. */
const Title = styled("h3")<{ large?: boolean }>(({ large }) => ({
  /** Figma: year row ends at y=9409, title starts at y=9439. */
  margin: "47px 0 0",
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: large ? fluid(30, 45) : fluid(28, 40),
  lineHeight: "54px",
  letterSpacing: "-0.047em",
  color: "#FFFFFF",
}));

const Subtitle = styled("p")({
  margin: "25px 0 0",
  maxWidth: 702,
  fontFamily: font.sans,
  fontWeight: 300,
  fontSize: 21.3,
  lineHeight: "32px",
  fontStyle: "italic",
  color: w(0.5),

  [bp.sm]: { fontSize: 17, lineHeight: "26px" },
});

const Description = styled("p")({
  margin: "40px 0 0",
  maxWidth: 727,
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: 20.1,
  lineHeight: "36.1px",
  color: w(0.65),

  [bp.sm]: { fontSize: 16, lineHeight: "28px" },
});

/**
 * Pull-quote: a left hairline running the full block height, with the 25px
 * quote glyph straddling it at the top (Figma: rule at x=803, icon frame at
 * x=789, text at x=839). The glyph carries the page background so it punches
 * a hole in the rule behind it.
 */
const Quote = styled("div")({
  position: "relative",
  maxWidth: 727,
  /** Figma: description ends at y=9699, quote starts at y=9749. */
  marginTop: 50,
  paddingLeft: 36,

  "& .rule": {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 1.25,
    background: w(0.3),
  },
  "& .quote-icon": {
    position: "absolute",
    left: -14,
    top: 0,
  },
  "& p": {
    fontFamily: font.sans,
    fontWeight: 400,
    fontSize: fluid(19, 25.1),
    lineHeight: "37.6px",
    letterSpacing: "-0.015em",
    fontStyle: "italic",
    color: w(0.85),
    margin: 0,
  },

  [bp.sm]: {
    "& p": { lineHeight: "30px" },
  },
});

const Chapters = styled("div")({
  maxWidth: 727,
  marginTop: 50,
});

const ChapterList = styled("div")({
  marginTop: 20,
  borderTop: `1.25px solid ${w(0.05)}`,
});

const ChapterRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 24,
  padding: "15px 0",
  borderBottom: `1.25px solid ${w(0.05)}`,

  "&:last-of-type": { borderBottom: "none" },

  "& .name": {
    fontFamily: font.sans,
    fontWeight: 400,
    fontSize: 17.5,
    lineHeight: "26.3px",
    color: w(0.65),
  },
  "& .no": {
    fontFamily: font.sans,
    fontWeight: 500,
    fontSize: 12.5,
    lineHeight: "18.8px",
    letterSpacing: "0.1em",
    color: w(0.3),
  },
});

/**
 * Footer strip: pages / released stats, then the CTA. Figma does not right-align
 * the button — it sits a fixed distance after the stats (174px on book 1,
 * 157px on book 2), so the offset is passed per book.
 */
const BookFooter = styled("div")<{ ctaOffset?: number }>(({ ctaOffset = 174 }) => ({
  display: "flex",
  alignItems: "center",

  /** Figma: chapters end at y=10142, this strip starts at y=10192. */
  marginTop: 50,
  paddingTop: 33,
  borderTop: `1.25px solid ${w(0.06)}`,

  "& > a": {
    marginLeft: ctaOffset,
    flexShrink: 0,
  },

  [bp.lg]: {
    justifyContent: "space-between",
    "& > a": { marginLeft: "auto" },
  },
  [bp.sm]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 24,
    "& > a": { marginLeft: 0 },
  },
}));

const Facts = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 35,
});

const FactDivider = styled("div")({
  width: 1,
  height: 40,
  background: w(0.1),
});

const FactValue = styled("div")({
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 22.6,
  lineHeight: "33.8px",
  letterSpacing: "-0.028em",
  color: w(0.85),
});

const FactLabel = styled("div")({
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 13.8,
  lineHeight: "20.7px",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: w(0.35),
});

/** One book: the whole block rises in, its cover eases up, its pull-quote rule draws down. */
const BookBlock: FC<{ book: BookData }> = ({ book }) => {
  const flip = book.coverSide === "right";
  const cover = useReveal({ variant: "scale", threshold: 0.2 });
  const copy = useReveal({ delay: 120 });
  const rule = useDraw({ delay: 150 });

  return (
    <Book flip={flip}>
      <CoverCell order={flip ? 1 : 0} overhang={flip}>
        <Cover ref={cover.ref} style={cover.style} src={book.cover} alt={book.title} loading="lazy" />
      </CoverCell>

      <CopyCell ref={copy.ref} style={copy.style} order={flip ? 0 : 1} offset={flip ? 58 : 40}>
        <Year>{book.year}</Year>
        <Title large={flip}>{book.title}</Title>
        <Subtitle>{book.subtitle}</Subtitle>
        <Description>{book.description}</Description>

        <Quote>
          <div className="rule" ref={rule.ref} style={rule.style} />
          <QuoteIcon className="quote-icon" />
          <p>{book.quote}</p>
        </Quote>

        <Chapters>
          <Eyebrow>Selected Chapters</Eyebrow>
          <ChapterList>
            {book.chapters.map((c) => (
              <ChapterRow key={c.no}>
                <span className="name">{c.title}</span>
                <span className="no">{c.no}</span>
              </ChapterRow>
            ))}
          </ChapterList>
        </Chapters>

        <BookFooter ctaOffset={flip ? 157 : 174}>
          <Facts>
            <div>
              <FactValue>{book.pages}</FactValue>
              <FactLabel>Pages</FactLabel>
            </div>
            <FactDivider />
            <div>
              <FactValue>{book.released}</FactValue>
              <FactLabel>Released</FactLabel>
            </div>
          </Facts>

          <PrimaryButton href={mailTo(`Request a copy: "${book.title}"`)} from="#575B63">
            Request a copy
            <ArrowUpRight size={18} color={w(0.8)} />
          </PrimaryButton>
        </BookFooter>
      </CopyCell>
    </Book>
  );
};

export const Books: FC = memo(() => {
  const heading = useReveal();

  return (
    <Section id="books">
      <Container width={1715}>
        <Heading ref={heading.ref} style={heading.style}>
          {"Long-form thinking\non finance, intelligence,\nand the spaces in between."}
        </Heading>

        {books.map((book) => (
          <BookBlock key={book.title} book={book} />
        ))}
      </Container>
    </Section>
  );
});
